import Layout from "../../../shared/Layout"
import ImagesSection from "./ImagesSection"
import type { DayOption, ImageItem } from "./ImagesSection"
import type { ImageOverlayLabels } from "./ImageOverlay"

// Re-exported for compatibility -- `ImageOverlay.tsx` and this file both
// need the per-image shape, which now lives in `ImagesSection.tsx` (t030
// impl-section, plan Extraction B).
export type { ImageItem }

interface ImagesProps {
  images: ImageItem[]
  // FR-10.2: pluralized, server-composed, `null` when the library is
  // empty -- the contract hides the counter entirely at zero rather than
  // rendering "0 картинок".
  countText: string | null
  // FR-4.3: `uploading` is a format string ("Загружаем %{done} из
  // %{total}…") interpolated client-side -- the words themselves stay
  // server-owned, only the two numbers are filled in (`ImagesSection.tsx`'s
  // own `formatUploadingLabel`).
  labels: { add: string; empty: string; uploading: string } & ImageOverlayLabels
  // t031 impl-read (FR-3.5): `#index`'s own top-level key (this action
  // composes its props directly, never through `images_section_props` --
  // see that method's own class comment) -- threaded straight through to
  // `ImagesSection`, which a later child wires into the overlay.
  days: DayOption[]
}

// The public picture library — the fourth public tab's own page
// (Images::ImagesController#index/#create render it). Props mirror
// exactly what the controller sends: every thumbnail's alt and every
// URL already resolved server-side (no date/time formatting, no variant
// URL construction here).
//
// t030 impl-section (plan Extraction B): this page's own body (the
// counter, the dashed upload button, the sequential batch, the grid and
// the overlay) has moved to `ImagesSection.tsx`, the component
// `Day.tsx`'s own last section also renders -- `ImagesSection` returns a
// **fragment**, so its children stay direct children of `Layout`'s own
// `flex flex-col gap-4` exactly as before this extraction. This page's
// own props are deliberately unchanged (`images`, not `items`; no
// `uploadPath`): `#index`'s wire shape stays pinned by FR-7.1 and by
// this repository's own landed request spec, so `/images` is hard-coded
// here rather than threaded through as a prop. (The day page's own
// `uploadPath` is composed server-side from the `day_images` route
// helper in `Schedule::DaysController#images_prop`.)
export default function Images({ images, countText, labels, days }: ImagesProps) {
  return (
    <Layout>
      <ImagesSection
        items={images}
        countText={countText}
        labels={labels}
        uploadPath="/images"
        emptyLine={labels.empty}
        days={days}
      />
    </Layout>
  )
}
